import { ConnectionListResult, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionDownstreamMetricsFilteredByTableQuery,
  VirtualConnectionDownstreamMetricsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamMetricsConnection_nodes,
  VirtualConnectionDownstreamMetricsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamMetricsFilteredByTableQuery';
import {
  VirtualConnectionDownstreamMetricsQuery,
  VirtualConnectionDownstreamMetricsQueryVariables,
  VirtualConnectionDownstreamMetricsQuery_virtualConnections_downstreamMetricsConnection_nodes,
} from '../../../gen-src/queries/VirtualConnectionDownstreamMetricsQuery';
import { MetricSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  VirtualConnectionDownstreamMetricsFilteredByTableQueryGql,
} from '../../queries/VirtualConnectionDownstreamMetricsFilteredByTableQueryGql';
import { VirtualConnectionDownstreamMetricsQueryGql } from '../../queries/VirtualConnectionDownstreamMetricsQueryGql';
import { getRowDataForMetric, MetricsDisplayGrid, MetricsDisplayGridRowData } from '../sharedDisplayGrid/MetricsDisplayGrid';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

type QueryVariables = VirtualConnectionDownstreamMetricsQueryVariables;
type QueryResults = VirtualConnectionDownstreamMetricsQuery;
type QueryResultListNode = VirtualConnectionDownstreamMetricsQuery_virtualConnections_downstreamMetricsConnection_nodes;
type GridRowData = MetricsDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionDownstreamMetricsFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionDownstreamMetricsFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionDownstreamMetricsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamMetricsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  return virtualConnection ? virtualConnection.downstreamMetricsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const table = virtualConnectionTables && virtualConnectionTables[0];
  return table ? table.downstreamMetricsConnection : null;
}

export type VirtualConnectionDownstreamMetricsQueryGridProps =
  GridSortProps<MetricsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function VirtualConnectionDownstreamMetricsQueryGrid(props: VirtualConnectionDownstreamMetricsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamMetricsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredMetricsLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamMetricsFilteredByTableQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as MetricSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForMetric}
    >
      {({ rowStore }) => <MetricsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamMetricsQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as MetricSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForMetric}
    >
      {({ rowStore }) => <MetricsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  );
}
